import type { FeePreview, TimeSlot, TablePricingRule, VenueData } from '../types';
import { tableSlotKey } from './tableReservationEligibility';

/**
 * Table line total in **major currency units** (as returned on `table_pricing` rows: use API numbers
 * as-is). `ticket_tier_bare_amount` and `free_tickets_count` apply when `table_ticket_tier_id` is set.
 */
/**
 * Copy `fee_preview` / `formatted_for_display` from a seat-map or events-seats `table_pricing` row
 * onto a `TablePricingRule` built locally (so tooltips / Payment see all-in display).
 */
export function attachTablePricingFromApiRow(rule: TablePricingRule, raw: unknown): void {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
  const o = raw as Record<string, unknown>;
  const ffd = o.formatted_for_display;
  if (typeof ffd === 'string' && ffd.trim() !== '') {
    rule.formatted_for_display = ffd;
  }
  const fp = o.fee_preview;
  if (fp != null && typeof fp === 'object' && !Array.isArray(fp)) {
    rule.fee_preview = fp as FeePreview;
  }
}

export function tablePriceFromEntry(raw: unknown, chairCount: number = 1): number {
  if (raw == null) return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.round(raw);
  if (typeof raw === 'object' && raw) {
    const o = raw as Record<string, unknown>;
    const num = (k: string): number | null => {
      const v = o[k];
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    };

    const fp = o.fee_preview;
    if (fp && typeof fp === 'object' && !Array.isArray(fp)) {
      const tw = (fp as FeePreview).total_with_fees;
      if (typeof tw === 'number' && Number.isFinite(tw) && tw >= 0) {
        return tw / 100;
      }
    }

    const tableBase =
      num('bare_amount') ??
      num('price') ??
      num('amount') ??
      num('total') ??
      num('fee') ??
      num('original_amount') ??
      0;

    const freeCount =
      typeof o.free_tickets_count === 'number' && Number.isFinite(o.free_tickets_count)
        ? Math.max(0, o.free_tickets_count)
        : 0;
    const perChair = num('ticket_tier_bare_amount') ?? 0;
    const hasChairTier = o.table_ticket_tier_id != null && String(o.table_ticket_tier_id) !== '' && perChair > 0;
    const chairs = Math.max(1, Math.floor(chairCount));

    if (hasChairTier) {
      const paid = Math.max(0, chairs - freeCount);
      return Math.round(tableBase + paid * perChair);
    }
    return Math.round(tableBase);
  }
  return 0;
}

function selectionKey(
  tableId: string,
  slot: { day: string; start_time: string; end_time: string },
): string {
  return `${tableId}-${slot.day}-${slot.start_time}-${slot.end_time}`;
}

function timeSlotFromUnknown(ts: unknown): { day: string; start_time: string; end_time: string } | null {
  if (!ts || typeof ts !== 'object') return null;
  const t = ts as Record<string, unknown>;
  const day = t.day;
  const start = t.start_time;
  const end = t.end_time;
  if (typeof day === 'string' && typeof start === 'string' && typeof end === 'string') {
    return { day, start_time: start, end_time: end };
  }
  return null;
}

/**
 * Flattens `table_pricing` array from events-seats into a map keyed like `table_availability`
 * (`tableId`, `tableId-day-starts-ends` via `tableSlotKey` / `selectionKey`, and `tableId-day-start`).
 */
export function tablePricingArrayToRecord(rows: unknown[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const tableId = typeof o.table_id === 'string' ? o.table_id : null;
    if (!tableId) continue;
    const slot = timeSlotFromUnknown(o.time_slot);
    if (!slot) {
      out[tableId] = row;
      continue;
    }
    out[tableSlotKey(tableId, slot)] = row;
    out[selectionKey(tableId, slot)] = row;
    out[`${tableId}-${slot.day}-${slot.start_time}`] = row;
  }
  return out;
}

export function normalizeTablePricingToRecord(input: unknown): Record<string, unknown> {
  if (input == null) return {};
  if (Array.isArray(input)) return tablePricingArrayToRecord(input);
  if (typeof input === 'object' && !Array.isArray(input)) {
    return input as Record<string, unknown>;
  }
  return {};
}

function getChairsForTable(venue: VenueData | null | undefined, tableId: string): number {
  const n = venue?.tables?.find((t) => t.id === tableId)?.chairs;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 1) return Math.floor(n);
  return 1;
}

function toTablePricingRule(
  row: unknown,
  getChairs: (tableId: string) => number,
): TablePricingRule | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const table_id = String(o.table_id ?? '');
  if (!table_id) return null;
  const chairs = getChairs(table_id);
  const price = tablePriceFromEntry(o, chairs);
  const ts = o.time_slot;
  const time_slot: TimeSlot | null =
    ts && typeof ts === 'object'
      ? (() => {
          const t = ts as Record<string, unknown>;
          if (
            typeof t.day === 'string' &&
            typeof t.start_time === 'string' &&
            typeof t.end_time === 'string'
          ) {
            return { day: t.day, start_time: t.start_time, end_time: t.end_time };
          }
          return null;
        })()
      : null;
  const fp = o.fee_preview;
  const fee_preview: FeePreview | undefined =
    fp != null && typeof fp === 'object' && !Array.isArray(fp) ? (fp as FeePreview) : undefined;
  const ffd = o.formatted_for_display;
  const formatted_for_display =
    typeof ffd === 'string' && ffd.trim() !== '' ? ffd : undefined;
  return {
    table_id,
    fee_tier_id: String(o.fee_tier_id ?? ''),
    price,
    currency: String(o.currency ?? 'USD'),
    label: String(o.label ?? ''),
    time_slot,
    free_tickets_count:
      typeof o.free_tickets_count === 'number' && o.free_tickets_count >= 0
        ? o.free_tickets_count
        : 0,
    ...(fee_preview != null ? { fee_preview } : {}),
    ...(formatted_for_display != null ? { formatted_for_display } : {}),
  };
}

/**
 * For widget seat map store: `table_pricing` as API rows; `price` is stored in **major** units
 * (use with `formatCurrencyMajor` in `TableMap`).
 */
export function tablePricingForSeatMapFromApi(
  input: unknown,
  venue: VenueData | null | undefined,
): TablePricingRule[] {
  const getChairs = (id: string) => getChairsForTable(venue, id);
  if (Array.isArray(input)) {
    return input
      .map((row) => toTablePricingRule(row, getChairs))
      .filter((r): r is TablePricingRule => r != null);
  }
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const vals = Object.values(input as Record<string, unknown>);
    if (vals.length > 0 && vals.every((v) => v && typeof v === 'object' && (v as { table_id?: string }).table_id)) {
      return vals
        .map((row) => toTablePricingRule(row, getChairs))
        .filter((r): r is TablePricingRule => r != null);
    }
  }
  return [];
}
